/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input, InputPassword } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { signIn } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import React, { Suspense, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Person from "@/components/icons/Person";
import Organization from "@/components/icons/Organization";
import { ArrowLeftIcon } from "lucide-react";

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/my-course";
  const [selectedUserType, setSelectedUserType] = useState<string>("");
  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
      remember: false,
    },
    mode: "onChange",
  });

  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    localStorage.removeItem("chat_thread");
    localStorage.removeItem("session_id_store");
    localStorage.removeItem("session_id_to_send_store");
    localStorage.removeItem("chat_modal_status");
    localStorage.removeItem("chat_heartbeat");
    localStorage.removeItem("timestamp");
    localStorage.removeItem("requested_admin_support");
    localStorage.removeItem("mark_solved_last_reset");
    localStorage.setItem("fresh_login", "true");
    localStorage.setItem("placeholder_show", "false");

    try {
      const result = await signIn("credentials", {
        redirect: false,
        email: data.email,
        password: data.password,
        remember: data.remember,
        callbackUrl: callbackUrl,
      });

      if (result?.ok) {
        // setSelectedUserType("");
        if (result.url) {
          if (typeof window !== "undefined") {
            const url = new URL(result.url);
            if (url.origin === window.location.origin) {
              router.push(url.pathname + url.search);
            } else {
              router.push("/my-course");
            }
          }
          // const url = new URL(result.url);
          // if (url.origin === window.location.origin) {
          //   router.push(url.pathname + url.search);
          // }
        } else {
          router.push("/my-course");
        }
      } else if (result?.error) {
        setError("root", {
          type: "manual",
          message: result.error || "Invalid credentials",
        });
      }
    } catch (error) {
      setError("root", {
        type: "manual",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {selectedUserType === "" ? (
        <>
          <div className="flex items-center justify-center pt-20 lg:pt-28">
            <div className="grid max-w-[90vw] min-w-[50vw] grid-cols-1 gap-4 rounded-2xl border border-gray-200 p-6 md:gap-6 lg:grid-cols-2">
              <div className="text-center text-2xl font-semibold text-gray-800 md:col-span-2 md:mb-4 md:text-4xl">
                Choose Your Account Type
              </div>
              <div
                onClick={() => {
                  setSelectedUserType("trainee");
                }}
                className="group cursor-pointer space-y-3 rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 ease-in-out hover:bg-blue-500 hover:text-white"
              >
                <div className="flex flex-col items-center justify-center gap-3">
                  <Person className="size-7 text-blue-600 transition-all duration-300 ease-in-out group-hover:text-white md:size-10" />
                  <p className="text-xl font-semibold text-blue-600 transition-all duration-300 ease-in-out group-hover:text-white md:text-2xl">
                    Continue as Trainee
                  </p>
                </div>
                <div className="text-sm text-gray-500 transition-all duration-300 ease-in-out group-hover:text-white md:text-base">
                  <p className="mb-4 text-sm md:text-base">For Team Members</p>
                  <div className="pl-7">
                    <ul className="list-disc font-normal">
                      <li>Access assigned training</li>
                      <li>No account creation required</li>
                      <li>Login via invitation email</li>
                    </ul>
                  </div>
                </div>
              </div>
              <Link
                href={`${process.env.NEXT_PUBLIC_ADMIN_PANEL_BASE_URL}/login`}
              >
                <div className="group cursor-pointer space-y-3 rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 ease-in-out hover:bg-blue-500 hover:text-white">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Organization className="size-7 text-blue-600 transition-all duration-300 ease-in-out group-hover:text-white md:size-10" />
                    <p className="text-xl font-semibold text-blue-600 transition-all duration-300 ease-in-out group-hover:text-white md:text-2xl">
                      Continue as Organization
                    </p>
                  </div>
                  <div className="text-sm text-gray-500 transition-all duration-300 ease-in-out group-hover:text-white md:text-base">
                    <p className="mb-4 text-sm md:text-base">
                      Agents managing Trainees
                    </p>
                    <div className="pl-7">
                      <ul className="list-disc font-normal">
                        <li>Create Trainee accounts</li>
                        <li>Manage and control Trainee</li>
                        <li>Track progress</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-12 pt-20 lg:pt-28 xl:col-span-6 xl:-mt-10 2xl:mt-0">
              <div className="flex flex-col justify-between space-y-2 px-4 sm:items-center xl:space-y-4 2xl:space-y-6">
                <div className="sm:w-[73%]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-0 text-2xl text-blue-600"
                    onClick={() => setSelectedUserType("")}
                  >
                    <ArrowLeftIcon className="size-6" />
                    Back
                  </Button>
                </div>
                <div className="space-y-2 sm:w-[70%] xl:space-y-4 2xl:space-y-6">
                  {/* Title */}
                  <div className="space-y-2">
                    <h2 className="text-primary font-inter text-2xl font-semibold xl:text-4xl">
                      Trainee Login
                    </h2>

                    {/* Subtitle */}
                    <p className="text-base font-normal text-slate-800 xl:text-lg">
                      Log in to continue your training and access your
                      personalized dashboard.
                    </p>
                  </div>

                  {/* Root Error Display */}
                  {errors.root && (
                    <div className="rounded-md bg-red-50 p-3 text-red-600">
                      {errors.root.message}
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-6 space-y-4">
                      {/* Email Field */}
                      <div className="mb-4 space-y-2 lg:mb-6">
                        <Label htmlFor="email">
                          Personal Email <span className="text-red-500">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="email"
                          rules={{
                            required: "Email is required",
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: "Invalid email address",
                            },
                          }}
                          render={({
                            field: { onChange, value, onBlur },
                            fieldState: { error },
                          }) => (
                            <Input
                              type="text"
                              placeholder="Enter email address"
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value}
                              errorText={error?.message}
                              aria-invalid={!!error}
                              id="email"
                              name="email"
                            />
                          )}
                        />
                      </div>

                      {/* Password Field */}
                      <div className="space-y-2">
                        <Controller
                          control={control}
                          name="password"
                          rules={{
                            required: "Password is required",
                            minLength: {
                              value: 8,
                              message: "Password must be at least 8 characters",
                            },
                          }}
                          render={({
                            field: { onChange, value, onBlur },
                            fieldState: { error },
                          }) => (
                            <div className="space-y-2">
                              <Label htmlFor="password">
                                Password <span className="text-red-500">*</span>
                              </Label>
                              <InputPassword
                                id="password"
                                placeholder="Enter password"
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                errorText={error?.message}
                                aria-invalid={!!error}
                              />
                            </div>
                          )}
                        />
                      </div>

                      {/* Remember/Forgot Password */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Controller
                            control={control}
                            name="remember"
                            render={({ field: { onChange, value } }) => (
                              <Checkbox
                                id="remember"
                                checked={value}
                                onCheckedChange={onChange}
                              />
                            )}
                          />
                          <Label
                            htmlFor="remember"
                            className="font-normal text-gray-700"
                          >
                            Keep me signed in
                          </Label>
                        </div>

                        <Link
                          href="/forget-password"
                          className="text-sm font-semibold text-slate-800 underline-offset-4 hover:underline lg:text-base"
                        >
                          Forgot password
                        </Link>
                      </div>
                    </div>
                    {/* Submit Button */}
                    <Button
                      className="h-9 w-full rounded-xl bg-blue-600 text-sm hover:bg-blue-600/90 xl:h-14 xl:text-lg"
                      // size="2xl"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? "Logging in..." : "Login as Trainee"}
                    </Button>
                  </form>

                  <div className="flex flex-col items-center justify-center">
                    <p className="text-base text-gray-600">Or</p>
                    <p className="text-lg font-medium text-gray-700">
                      Didn’t receive an invitation from organization?
                    </p>
                    <p className="text-lg font-semibold text-blue-600">
                      Contact your Organization admin
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden overflow-hidden xl:col-span-6 xl:block">
              {/* <AuthRightSidebar /> */}
              <div className="overflow-hidden">
                <Image
                  src={`/assets/auth/auth.png`}
                  className="w-full object-cover object-center"
                  alt="Auth Right Sidebar Image"
                  width={0}
                  height={0}
                  quality={100}
                  sizes="100vw"
                  priority
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// The main export wraps the form in Suspense
export default function Login() {
  return (
    <Suspense
      fallback={
        <div className="flex h-[calc(100vh-104px)] items-center justify-center py-20">
          Loading...
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
